import React, { useState, useContext, useEffect  } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import axios from "../../api/axiosInstance";
import Env from "../../runtimeEnv";
import TherapistSidebar from "../../components/TherapistSidebar";
import { AuthContext } from "../../context/AuthContext";
import { isDemoMode } from "../../demo/demoMode";

const TherapistCreateMeetingForm = () => {
  const location = useLocation();
const selectedClientEmail = location.state?.clientEmail;
  const { register, handleSubmit, setValue } = useForm();

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeNavItem, setActiveNavItem] = useState("create-meeting");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // 🔵 Get therapist email from AuthContext
  const { therapistEmail } = useContext(AuthContext);

  useEffect(() => {
  if (selectedClientEmail) {
    setValue("email", selectedClientEmail);
  }
}, [selectedClientEmail, setValue]);


  // 🔵 Autofill therapistEmail in the form on mount
  useEffect(() => {
    if (therapistEmail) {
      setValue("therapistEmail", therapistEmail);
    }
  }, [therapistEmail, setValue]);

  // 🔵 Handle form submit
  const onSubmit = async (formData) => {
    setLoading(true);
    setMessage(null);

    try {
      if (!formData.meetingName) {
        formData.meetingName = "Therapy Session";
      }

      formData.email = selectedClientEmail;

      // remove unnecessary values
      delete formData.therapist;

      if (isDemoMode) {
        setMessage({ type: "success", text: "Demo session scheduled locally." });
        return;
      }

      await axios.post(
        `${Env.REACT_APP_PYTHON_API_URL}/api/therapist/schedule`,
        formData
      , { withCredentials: true });

      setMessage({ type: "success", text: "Session scheduled successfully!" });
      setValue("therapistEmail", therapistEmail);
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to schedule session.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      {/* Sidebar */}
      <TherapistSidebar
        activeNavItem={activeNavItem}
        setActiveNavItem={setActiveNavItem}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
      />

      {/* Main Content */}
      <div className="w-full py-2">
        <div className="max-w-4xl mx-auto">

          {message && (
            <div
              className={`p-3 text-center rounded-md mb-6 ${
                message.type === "success"
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}
            >
              {message.text}
            </div>
          )}

          <div className="space-y-4">

            {/* Client Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Email
              </label>
              <input
                {...register("email")}
                readOnly
                placeholder="Client Email"
                required
                className="w-full min-h-12 px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Meeting Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meeting Name
              </label>
              <input
                {...register("meetingName")}
                placeholder="Meeting Name"
                required
                className="w-full min-h-12 px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Date & Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session Date & Time
              </label>
              <input
                type="datetime-local"
                {...register("sessionTime")}
                required
                className="w-full min-h-12 px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Therapist Email (Autofilled) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Therapist Email
              </label>
              <input
                {...register("therapistEmail")}
                readOnly
                className="w-full min-h-12 px-4 py-3 text-base border border-gray-300 rounded-xl bg-gray-100 cursor-not-allowed"
              />
            </div>

            <button
              type="submit"
              onClick={handleSubmit(onSubmit)}
              className="w-full min-h-12 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-full shadow-sm transition"
              disabled={loading || !selectedClientEmail}
            >
              {loading ? "Scheduling..." : "Schedule Session"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TherapistCreateMeetingForm;
